import type { ClimateAction } from "@/domain/climate-action/models/climate-action";
import type { Project } from "@/domain/project/models/project-model";

import { formatLocaleDate, formatReductionImpact, formatUnitLabel, formatYearlyCosts } from "./field-format";
import { toDisplayProjectDate } from "./project-date-offset";
import { extractPlainTextFromTipTap } from "./tiptap-text";

interface IndicatorCurrentValueSource {
  title?: string | null;
  description?: string | null;
  frequency?: string | null;
  unit?: { title: string; symbol: string } | null;
}

export function extractIndicatorCurrentValue(indicator: IndicatorCurrentValueSource, fieldKey: string): string | null {
  switch (fieldKey) {
    case "indicatorTitle":
      return indicator.title ?? null;
    case "indicatorDescription":
      return extractPlainTextFromTipTap(indicator.description);
    case "unit":
      if (!indicator.unit) return null;
      return formatUnitLabel(indicator.unit.title, indicator.unit.symbol);
    case "frequency":
      return indicator.frequency ?? null;
    default:
      return null;
  }
}

export function extractCurrentDisplayValue(
  entity: ClimateAction | Project,
  fieldKey: string,
  locale = "de-DE",
): string | null {
  switch (fieldKey) {
    case "title":
      return entity.title ?? null;
    case "description":
      return extractPlainTextFromTipTap(entity.description);
    case "status":
      return entity.statusInstance?.status?.name ?? null;
    case "priority":
      return entity.priorityInstance?.priority?.name ?? null;
    case "phase":
      return entity.phaseInstance?.phase?.name ?? null;
    case "assignee": {
      const assigneeList = entity.assigneeList;
      if (!assigneeList || assigneeList.length === 0) return null;
      return assigneeList.map((a) => a.user?.fullname ?? a.user?.email ?? "").filter(Boolean).join(", ") || null;
    }
    case "focusArea":
      return entity.focusAreaInstance?.focusArea?.name ?? null;
    case "labels": {
      const labelList = entity.labelInstanceList;
      if (!labelList || labelList.length === 0) return null;
      return (
        labelList
          .map((l) => {
            const labelTitle = l.label?.title;
            if (!labelTitle) return null;
            const groupTitle = l.labelGroup?.title;
            return groupTitle ? `${groupTitle}: ${labelTitle}` : labelTitle;
          })
          .filter(Boolean)
          .join(", ") || null
      );
    }
    case "startDate":
      return formatLocaleDate(toDisplayProjectDate(entity.startDate), locale);
    case "endDate":
      return formatLocaleDate(toDisplayProjectDate(entity.endDate), locale);
    case "yearlyCosts":
      return formatYearlyCosts(entity.yearlyCosts, locale);
    case "reductionImpact":
      if ("reductionImpact" in entity) {
        return formatReductionImpact((entity as ClimateAction).reductionImpact, locale);
      }
      return null;
    default:
      return null;
  }
}
